import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { updateMetaTags, addStructuredData, getSiteUrl } from '../../utils/seo';

const ContactoPage = () => {
  useEffect(() => {
    const siteUrl = getSiteUrl();
    
    updateMetaTags({
      title: 'Contacto - Vaciado de Pisos El Prat | JUSTCLEAN ELPRAT',
      description: 'Contacta con JUSTCLEAN ELPRAT para vaciado de pisos en El Prat de Llobregat. Presupuesto gratis en 24h. ☎ 602 36 59 96 | info@elprat.justclean.es',
      keywords: 'contacto vaciado pisos El Prat, presupuesto gratis, teléfono limpieza Diógenes',
      path: '/contacto'
    });

    // Add ContactPage Schema
    const contactSchema = addStructuredData({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contacto JUSTCLEAN ELPRAT",
      "description": "Contacta con nosotros para solicitar presupuesto gratuito de vaciado de pisos en El Prat de Llobregat",
      "url": `${siteUrl}/contacto`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "JUSTCLEAN ELPRAT",
        "telephone": "+34602365996",
        "email": "info@elprat.justclean.es",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Carrer de la Verge de Montserrat, 45",
          "addressLocality": "El Prat de Llobregat",
          "addressRegion": "Barcelona",
          "postalCode": "08820",
          "addressCountry": "ES"
        }
      }
    });

    return () => {
      document.head.removeChild(contactSchema);
    };
  }, []);

  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    servicio: '',
    mensaje: '',
    privacidad: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacidad) {
      alert('Debes aceptar la política de privacidad');
      return;
    }

    if (formData.mensaje.length > 500) {
      alert('El mensaje no puede superar los 500 caracteres');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        nombre: formData.nombre,
        email: formData.email,
        telefono: formData.telefono,
        servicio: formData.servicio,
        mensaje: formData.mensaje,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d4q1031fcjnktac9qmmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          servicio: '',
          mensaje: '',
          privacidad: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const zonasPrat = [
    'Centre',
    'Eixample',
    'Sant Cosme',
    'La Granja',
    'Jardins de la Pau',
    'Zona Delta',
    'Les Moreres',
    'Can Tries',
  ];

  const localidadesCercanas = [
    'Viladecans',
    'Gavà',
    'Sant Boi',
    'Cornellà',
    'L\'Hospitalet',
    'Barcelona Sur',
    'Castelldefels',
    'Esplugues',
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="py-16 sm:py-20 bg-gradient-to-b from-light to-white pt-28 sm:pt-32">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="mb-4 text-xs sm:text-sm">
              <a href="/" className="text-secondary hover:text-primary">
                Inicio
              </a>
              <span className="text-secondary mx-2">&gt;</span>
              <span className="text-dark font-medium">Contacto</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-dark mb-6 break-words px-2">
              Contacta con JUSTCLEAN ELPRAT
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-secondary px-2">
              Presupuesto gratuito en menos de 24 horas para <strong>vaciado de pisos</strong> y <strong>limpieza extrema</strong> en El Prat de Llobregat. <a href="/servicios" className="text-primary hover:underline font-semibold">Ver todos nuestros servicios</a>
            </p>
          </div>
        </section>

        {/* Main Contact Section */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Contact Info */}
              <div className="lg:col-span-1">
                <div className="bg-light rounded-2xl p-6 lg:sticky lg:top-24">
                  <h3 className="text-xl sm:text-2xl font-bold text-dark mb-6">Información de Contacto</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-phone-line text-primary text-xl"></i>
                        </div>
                        <h4 className="font-bold text-dark">Teléfono</h4>
                      </div>
                      <a
                        href="tel:+34602365996"
                        className="text-dark/80 hover:text-primary transition-colors ml-13 cursor-pointer break-all"
                      >
                        602 36 59 96
                      </a>
                    </div>

                    <div className="border-t border-secondary/20 pt-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-mail-line text-primary text-xl"></i>
                        </div>
                        <h4 className="font-bold text-dark">Email</h4>
                      </div>
                      <a
                        href="mailto:info@elprat.justclean.es"
                        className="text-dark/80 hover:text-primary transition-colors ml-13 cursor-pointer break-all text-sm sm:text-base"
                      >
                        info@elprat.justclean.es
                      </a>
                    </div>

                    <div className="border-t border-secondary/20 pt-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-map-pin-line text-primary text-xl"></i>
                        </div>
                        <h4 className="font-bold text-dark">Dirección</h4>
                      </div>
                      <p className="text-dark/80 ml-13 text-sm sm:text-base">
                        Carrer de la Verge de Montserrat, 45<br />
                        08820 El Prat de Llobregat<br />
                        Barcelona, España
                      </p>
                    </div>

                    <div className="border-t border-secondary/20 pt-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-time-line text-primary text-xl"></i>
                        </div>
                        <h4 className="font-bold text-dark">Horario</h4>
                      </div>
                      <p className="text-dark/80 ml-13 text-sm sm:text-base">
                        Lunes a Domingo<br />
                        <strong>Servicio 24 horas</strong>
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-secondary/20">
                    <h4 className="font-bold text-dark mb-3">Síguenos</h4>
                    <div className="flex gap-3">
                      <a
                        href="#"
                        className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center hover:bg-primary hover:text-white transition-colors cursor-pointer"
                        aria-label="Facebook"
                      >
                        <i className="ri-facebook-fill"></i>
                      </a>
                      <a
                        href="#"
                        className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center hover:bg-primary hover:text-white transition-colors cursor-pointer"
                        aria-label="Instagram"
                      >
                        <i className="ri-instagram-line"></i>
                      </a>
                      <a
                        href="#"
                        className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center hover:bg-primary hover:text-white transition-colors cursor-pointer"
                        aria-label="LinkedIn"
                      >
                        <i className="ri-linkedin-fill"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Center Column - Contact Form */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl border border-secondary/20 p-8">
                  <h3 className="text-2xl font-bold text-dark mb-6">Solicita tu Presupuesto Gratis</h3>
                  
                  <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                    <div>
                      <label htmlFor="nombre" className="block text-sm font-medium text-dark mb-2">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-b-2 border-secondary/30 focus:border-primary outline-none transition-colors text-dark bg-transparent"
                        placeholder="Tu nombre"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-dark mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-b-2 border-secondary/30 focus:border-primary outline-none transition-colors text-dark bg-transparent"
                        placeholder="tu@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="telefono" className="block text-sm font-medium text-dark mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-b-2 border-secondary/30 focus:border-primary outline-none transition-colors text-dark bg-transparent"
                        placeholder="602 36 59 96"
                      />
                    </div>

                    <div>
                      <label htmlFor="servicio" className="block text-sm font-medium text-dark mb-2">
                        Servicio que necesitas *
                      </label>
                      <select
                        id="servicio"
                        name="servicio"
                        value={formData.servicio}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-b-2 border-secondary/30 focus:border-primary outline-none transition-colors text-dark bg-transparent cursor-pointer"
                      >
                        <option value="">Selecciona un servicio</option>
                        <option value="Vaciado de Pisos y Casas">Vaciado de Pisos y Casas</option>
                        <option value="Vaciado de Locales Comerciales">Vaciado de Locales Comerciales</option>
                        <option value="Retirada de Muebles">Retirada de Muebles y Electrodomésticos</option>
                        <option value="Limpieza Diógenes">Limpieza por Síndrome de Diógenes</option>
                        <option value="Pintura Final">Pintura Final</option>
                        <option value="Pequeñas Reparaciones">Pequeñas Reparaciones</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="mensaje" className="block text-sm font-medium text-dark mb-2">
                        Mensaje (máx. 500 caracteres) *
                      </label>
                      <textarea
                        id="mensaje"
                        name="mensaje"
                        value={formData.mensaje}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-3 border-b-2 border-secondary/30 focus:border-primary outline-none transition-colors text-dark bg-transparent resize-none"
                        placeholder="Cuéntanos qué necesitas..."
                      ></textarea>
                      <p className="text-xs text-secondary mt-1">
                        {formData.mensaje.length}/500 caracteres
                      </p>
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="privacidad"
                        name="privacidad"
                        checked={formData.privacidad}
                        onChange={handleChange}
                        required
                        className="mt-1 cursor-pointer"
                      />
                      <label htmlFor="privacidad" className="text-sm text-dark/70">
                        Acepto la{' '}
                        <a href="#" className="text-primary hover:underline">
                          política de privacidad
                        </a>{' '}
                        y el tratamiento de mis datos
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                        ¡Mensaje enviado! Te responderemos en menos de 24 horas.
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                        Error al enviar. Por favor, llámanos al 602 36 59 96.
                      </div>
                    )}

                    <p className="text-sm text-center text-secondary">
                      Respuesta en menos de 24 horas
                    </p>
                  </form>
                </div>
              </div>

              {/* Right Column - Service Areas */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl border border-secondary/20 p-6">
                  <h3 className="text-xl font-bold text-dark mb-4">Zonas que Cubrimos</h3>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-dark mb-3 flex items-center gap-2">
                      <i className="ri-map-pin-fill text-primary"></i>
                      Barrios de El Prat
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {zonasPrat.map((zona, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-dark/80">
                          <i className="ri-checkbox-circle-fill text-primary text-xs"></i>
                          {zona}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-secondary/20 pt-6">
                    <h4 className="font-semibold text-dark mb-3 flex items-center gap-2">
                      <i className="ri-map-2-fill text-primary"></i>
                      Localidades Cercanas
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {localidadesCercanas.map((localidad, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full"
                        >
                          {localidad}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-light rounded-lg">
                    <p className="text-sm text-dark/70 leading-relaxed">
                      Ofrecemos servicios de <strong>vaciado de pisos</strong>, <strong>vaciado de locales</strong> y <strong>limpieza por síndrome de Diógenes</strong> en toda la comarca del Baix Llobregat. <a href="/empresa" className="text-primary hover:underline font-semibold">Conoce más sobre nuestra empresa</a>
                    </p>
                  </div>
                </div>

                {/* Map */}
                <div className="mt-6 bg-white rounded-2xl overflow-hidden border border-secondary/20">
                  <div className="w-full h-64">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d47859.89171087!2d2.0758!3d41.3275!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a4992f3d6b7c8b%3A0x402359b7f3b3d60!2sEl%20Prat%20de%20Llobregat%2C%20Barcelona!5e0!3m2!1ses!2ses!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Ubicación El Prat de Llobregat"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Contact Methods */}
        <section className="py-16 sm:py-20 bg-primary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12 break-words">
              Contacto Inmediato
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a
                href="tel:+34602365996"
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 text-center hover:bg-white/20 transition-all group cursor-pointer"
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-phone-line text-3xl sm:text-4xl text-white"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Llamar</h3>
                <p className="text-white/80 mb-4 text-sm sm:text-base break-all">602 36 59 96</p>
                <span className="inline-flex items-center gap-2 text-white font-semibold text-sm sm:text-base">
                  Llamar Ahora
                  <i className="ri-arrow-right-line"></i>
                </span>
              </a>

              <a
                href="https://wa.me/34602365996"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 text-center hover:bg-white/20 transition-all group cursor-pointer"
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-whatsapp-line text-3xl sm:text-4xl text-white"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">WhatsApp</h3>
                <p className="text-white/80 mb-4 text-sm sm:text-base">Chatea con nosotros</p>
                <span className="inline-flex items-center gap-2 text-white font-semibold text-sm sm:text-base">
                  Abrir WhatsApp
                  <i className="ri-arrow-right-line"></i>
                </span>
              </a>

              <a
                href="mailto:info@elprat.justclean.es"
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 text-center hover:bg-white/20 transition-all group cursor-pointer"
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-mail-line text-3xl sm:text-4xl text-white"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Email</h3>
                <p className="text-white/80 mb-4 text-xs sm:text-sm md:text-base break-all px-2">info@elprat.justclean.es</p>
                <span className="inline-flex items-center gap-2 text-white font-semibold text-sm sm:text-base">
                  Enviar Email
                  <i className="ri-arrow-right-line"></i>
                </span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactoPage;
